import type { Maybe } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { Slot } from '@ils/slots/db/slot.js';
import { UserRole } from '@ils/static/enums.js';
import { DateTime, type Duration } from 'luxon';
import type { ExamDay } from '../db/examdays.js';

export const examSlotSelector = {
  _id: 1,
  candidateID: 1,
  tester: 1,
  startTime: 1,
  endTime: 1,
  osceID: 1,
  osce: {
    startButtonDuration: 1,
    taskType: 1,
    osceType: 1,
    durationInMinutes: 1,
    conversationType: 1,
    enableVideo: 1,
    titleTrainee: 1,
    videoFileID: 1,
  },
  answer: {
    type: 1,
    fileId: 1,
    onlyTraineeFileId: 1,
    text: 1,
    filesPresent: 1,
  },
  recordAudio: 1,
  roomID: 1,
  isCandidateMissing: 1,
} as const satisfies BaseQLSelectionSet<Slot>;

export const examDaySelector = {
  _id: 1,
  _class: 1,
  start: 1,
  end: 1,
  slotIDs: 1,
  slots: examSlotSelector,
} as const satisfies BaseQLSelectionSet<ExamDay>;

export type NarrowSlot = NarrowedEntity<Slot, typeof examSlotSelector>;

export enum ExamStateEnum {
  INIT = 'INIT', // init state -> not for actual in exam state handling
  PREPARE = 'PREPARE',
  STARTED_EARLY = 'STARTED_EARLY',
  RUNNING = 'RUNNING',
  PAUSE = 'PAUSE',
  FINISHED = 'FINISHED',
  NO_ACCESS = 'NO_ACCESS', // nutzer hat keinen Zugriff auf die slots
  NO_DAY = 'NO_DAY', // es gibt keine slots
  ERROR = 'ERROR', // generic error state
}

export interface ExamState {
  userRole: UserRole;
  userId: string;
  lastSlot?: Maybe<NarrowSlot>;
  slot?: Maybe<NarrowSlot>;
  index: number;
  slotCount: number;
  state: ExamStateEnum;
  topTimer: Duration<true>;
  progress: number;
  startButtonTimer: Duration<true>;
  startButtonProgress: number;
}

const dummyTime = DateTime.now();

export const initialExamState: ExamState = {
  userRole: UserRole.TRAINEE,
  userId: '',
  lastSlot: undefined,
  slot: undefined,
  index: 0,
  slotCount: 0,
  state: ExamStateEnum.INIT,
  topTimer: dummyTime.diff(dummyTime),
  progress: 0,
  startButtonTimer: dummyTime.diff(dummyTime),
  startButtonProgress: 0,
};

export const makeInitialExamState = (
  state: ExamStateEnum = ExamStateEnum.INIT,
  userId = '',
  userRole = UserRole.TRAINEE,
  slotCount = 0,
) => ({
  userRole,
  userId,
  lastSlot: undefined,
  slot: undefined,
  index: 0,
  slotCount,
  state,
  topTimer: dummyTime.diff(dummyTime),
  progress: 0,
  startButtonTimer: dummyTime.diff(dummyTime),
  startButtonProgress: 0,
});
